import React from "react";
import { SA_PROVINCES } from "../lib/saProvinces";

/*
 * Province label positions are taken from the actual SVG path centroids
 * (computed from simplemaps.com path data, viewBox 0 0 1000 1000).
 * Western Cape's bbox-centroid is offset due to the Prince Edward Islands,
 * so its label is manually placed near the mainland centroid.
 * Gauteng is tiny — small label, slightly offset to avoid overlap with MP.
 */
const LABELS = {
  LP:  { x: 557, y: 100, size: 16, name: "LIMPOPO" },
  MP:  { x: 595, y: 165, size: 11, name: "MPUMALANGA" },
  GP:  { x: 527, y: 174, size: 7,  name: "GAUTENG" },
  NW:  { x: 448, y: 184, size: 13, name: "NORTH WEST" },
  FS:  { x: 495, y: 263, size: 13, name: "FREE STATE" },
  KZN: { x: 608, y: 273, size: 11, name: "KWAZULU-NATAL" },
  NC:  { x: 315, y: 270, size: 16, name: "NORTHERN CAPE" },
  EC:  { x: 488, y: 380, size: 13, name: "EASTERN CAPE" },
  WC:  { x: 310, y: 410, size: 13, name: "WESTERN CAPE" },
};

// Tight viewBox around mainland SA (excludes the Prince Edward Islands tail)
const VB = { x: 165, y: 35, w: 670, h: 445 };

export const CoverageMap = ({
  coveredCodes = ["GP", "NW", "MP", "FS", "WC", "NC"],
}) => {
  return (
    <svg
      viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`}
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      aria-label="Diesel & Dust coverage map of South Africa"
      data-testid="coverage-map-svg"
    >
      <defs>
        <linearGradient id="orangeFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF9F33" />
          <stop offset="100%" stopColor="#F28A16" />
        </linearGradient>
        <filter id="provShadow" x="-5%" y="-5%" width="110%" height="110%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#000" floodOpacity="0.18" />
        </filter>
        <pattern id="mapBgDots" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1.2" cy="1.2" r="0.7" fill="#cfd7db" opacity="0.55" />
        </pattern>
      </defs>

      {/* Background plate */}
      <rect x={VB.x} y={VB.y} width={VB.w} height={VB.h} fill="#f5f6f6" />
      <rect x={VB.x} y={VB.y} width={VB.w} height={VB.h} fill="url(#mapBgDots)" />

      {/* Provinces */}
      <g filter="url(#provShadow)">
        {SA_PROVINCES.map((p) => {
          const covered = coveredCodes.includes(p.code);
          return (
            <path
              key={p.code}
              d={p.d}
              fill={covered ? "url(#orangeFill)" : "#9aa6ad"}
              stroke="#ffffff"
              strokeWidth="1.4"
              data-testid={`map-province-${p.code.toLowerCase()}`}
            />
          );
        })}
      </g>

      {/* Province labels */}
      <g style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, letterSpacing: "0.04em" }}>
        {SA_PROVINCES.map((p) => {
          const label = LABELS[p.code];
          if (!label) return null;
          const covered = coveredCodes.includes(p.code);
          return (
            <text
              key={`l-${p.code}`}
              x={label.x}
              y={label.y}
              textAnchor="middle"
              fontSize={label.size}
              fill={covered ? "#ffffff" : "#3f4b52"}
              style={{
                textTransform: "uppercase",
                paintOrder: "stroke",
                stroke: covered ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.65)",
                strokeWidth: covered ? 0.7 : 0.6,
              }}
            >
              {label.name}
            </text>
          );
        })}
      </g>

      {/* Compass rose — bottom right inside viewBox */}
      <g transform={`translate(${VB.x + VB.w - 50}, ${VB.y + VB.h - 50})`} data-testid="compass-rose">
        <circle r="32" fill="#ffffff" stroke="#cfd7db" strokeWidth="1.2" />
        <circle r="26" fill="none" stroke="#cfd7db" strokeWidth="0.6" />
        {/* North/South spear */}
        <polygon points="0,-26 4,0 0,26 -4,0" fill="#20282c" />
        {/* East/West spear */}
        <polygon points="-26,0 0,-4 26,0 0,4" fill="#f28a16" />
        <circle r="2.5" fill="#20282c" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <line
            key={deg}
            x1="0"
            y1="-30"
            x2="0"
            y2="-27"
            stroke="#20282c"
            strokeWidth="1"
            transform={`rotate(${deg})`}
          />
        ))}
        <text
          x="0"
          y="-36"
          textAnchor="middle"
          fontSize="10"
          fontWeight="900"
          fill="#20282c"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          N
        </text>
      </g>
    </svg>
  );
};

export default CoverageMap;
